<template>
  <div>
    <data-table
      ref="datatableRef"
      data-testid="franquias-configurations-table"
      :config="datatableConfig"
      :endpoint="endpoint"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface Props {
  franquiaId: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'edit-config': [configNome: Id];
}>();

const { t } = useI18n();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed(() => `/index.php/config/configuracoes_sistema_franquia/list_with_franquia_data/${props.franquiaId}`);

const tableColumns = computed(() => {
  return [
    {
      data: 'nome',
      title: t('Nome'),
      width: 250,
      minWidth: 200,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'descricao',
      title: t('Descrição'),
      flex: 1,
      minWidth: 300,
      resizable: true,
      sortable: true,
    },
    {
      data: 'valor_padrao',
      title: t('Valor padrão'),
      width: 150,
      minWidth: 120,
      resizable: true,
      sortable: true,
    },
    {
      data: 'valor',
      title: t('Valor'),
      width: 150,
      minWidth: 120,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellParams): string => {
        // Show valor or valor_padrao if valor is empty
        const row = (params.data || {}) as { valor_padrao?: string };
        const displayValue = (params.value as string | undefined) || row.valor_padrao || '';
        return displayValue;
      },
    },
  ];
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaConfiguracoes',
    title: t('Configurações do Sistema'),
    filename: `configuracoes_franquia_${props.franquiaId}_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 20,
    height: '500px',
    pagination: true,
    rowSelection: 'single',
    onEditClick: handleEditClick,
  };
});

// Methods
const loadData = (): void => {
  log('ConfigurationsTable', 'loadData', 'Triggering grid refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('ConfigurationsTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t(`Carregadas`) + ` ${data.length} ` + t(`configurações`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('ConfigurationsTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar configurações: ') + message);
};

const handleRowClick = (rowData: unknown): void => {
  const row = (rowData as { nome?: string } | undefined);
  log('ConfigurationsTable', 'handleRowClick', 'Row clicked', row?.nome || null);
  // Handle row click if needed
};

const handleEditClick = (configNome: Id): void => {
  log('ConfigurationsTable', 'handleEditClick', 'Edit clicked for config', configNome);
  emit('edit-config', configNome);
};

// Expose methods for parent components
defineExpose({
  loadData,
});
</script>

<style scoped>
/* No styles needed - DataTable handles everything */
</style>
